import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import { createUser } from '../../Action/api';

const AddUserDialog = ({ open, onClose, onSuccess }) => {
  console.log('Dialog props:', { open, onClose, onSuccess }); // Add this line

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    type: 'User',
    permissions: {
      dashboard: true,
      salesboard: false,
      users: false,
      leads: false,
      qualified: false,
      disqualified: false,
      closedWon: false,
      closedLoss: false,
      contacts: false,
      companies: false,
      opportunities: false,
      quotations: false,
      tasks: false,
      meetings: false,
      activities: false,
      reports: false,
      administration: false,
    }
  });
  
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (permission) => {
    setNewUser(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission]
      }
    }));
  };

  const handleClose = () => {
    setNewUser({
      name: '',
      email: '',
      type: 'User',
      permissions: {
        dashboard: true,
        salesboard: false,
        users: false,
        leads: false,
        qualified: false,
        disqualified: false,
        closedWon: false,
        closedLoss: false,
        contacts: false,
        companies: false,
        opportunities: false,
        quotations: false,
        tasks: false,
        meetings: false,
        activities: false,
        reports: false,
        administration: false,
      }
    });
    setFormError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!newUser.name || !newUser.email) {
      setFormError('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const requestPayload = {
        userId: newUser.email,
        password: "Default@123",
        staffFullName: newUser.name,
        personalEmail: newUser.email,
        roleName: newUser.type,
        menuList: [
          {
            menuId: "MENU-001",
            menuName: "Dashboard",
            permission: newUser.permissions.dashboard ? "yes" : "no"
          },
          {
            menuId: "MENU-002",
            menuName: "Sales Board",
            permission: newUser.permissions.salesboard ? "yes" : "no"
          },
          {
            menuId: "MENU-003",
            menuName: "Users",
            permission: newUser.permissions.users ? "yes" : "no"
          },
          {
            menuId: "MENU-004",
            menuName: "Leads",
            permission: newUser.permissions.leads ? "yes" : "no"
          },
          {
            menuId: "MENU-005",
            menuName: "Qualified",
            permission: newUser.permissions.qualified ? "yes" : "no"
          },
          {
            menuId: "MENU-006",
            menuName: "Disqualified",
            permission: newUser.permissions.disqualified ? "yes" : "no"
          },
          {
            menuId: "MENU-007",
            menuName: "Closed Won",
            permission: newUser.permissions.closedWon ? "yes" : "no"
          },
          {
            menuId: "MENU-008",
            menuName: "Closed Loss",
            permission: newUser.permissions.closedLoss ? "yes" : "no"
          },
          {
            menuId: "MENU-009",
            menuName: "Contacts",
            permission: newUser.permissions.contacts ? "yes" : "no"
          },
          {
            menuId: "MENU-010",
            menuName: "Companies",
            permission: newUser.permissions.companies ? "yes" : "no"
          },
          {
            menuId: "MENU-011",
            menuName: "Opportunities",
            permission: newUser.permissions.opportunities ? "yes" : "no"
          },
          {
            menuId: "MENU-012",
            menuName: "Quotations",
            permission: newUser.permissions.quotations ? "yes" : "no"
          },
          {
            menuId: "MENU-013",
            menuName: "Tasks",
            permission: newUser.permissions.tasks ? "yes" : "no"
          },
          {
            menuId: "MENU-014",
            menuName: "Meetings",
            permission: newUser.permissions.meetings ? "yes" : "no"
          },
          {
            menuId: "MENU-015",
            menuName: "Activities",
            permission: newUser.permissions.activities ? "yes" : "no"
          },
          {
            menuId: "MENU-016",
            menuName: "Reports",
            permission: newUser.permissions.reports ? "yes" : "no"
          },
          {
            menuId: "MENU-017",
            menuName: "Administration",
            permission: newUser.permissions.administration ? "yes" : "no"
          }
        ]
      };

      const response = await createUser(requestPayload);

      if (response.data && response.data.status) {
        handleClose();
        onSuccess(newUser.name, newUser.type);
      } else {
        setFormError(response.data?.message || 'Failed to create user. Please try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setFormError('An error occurred while creating the user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
        }
      }}
    >
      <Box sx={{ 
        background: 'linear-gradient(135deg, #4f46e5, #3730a3)',
        pt: 3, 
        pb: 4,
        px: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 120%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)',
        }} />
        <Typography variant="h5" sx={{ 
          color: 'white',
          fontWeight: 700,
          position: 'relative',
          mb: 1
        }}>
          Add New User
        </Typography>
        <Typography variant="body2" sx={{ 
          color: alpha('#fff', 0.7),
          position: 'relative'
        }}>
          Create a new user account with specific permissions
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3, mt: 1 }}>
        {formError && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': { alignItems: 'center' }
            }}
          >
            {formError}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Full Name"
              fullWidth
              required
              value={newUser.name}
              onChange={handleInputChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="email"
              label="Email Address"
              fullWidth
              required
              type="email"
              value={newUser.email}
              onChange={handleInputChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                name="type"
                value={newUser.type}
                label="User Type"
                onChange={handleInputChange}
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha('#4f46e5', 0.1)
                }}>
                  <LockPersonIcon sx={{ color: '#4f46e5' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#1e293b', mb: 0.5 }}>
                    Module Permissions
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Select the modules this user can access
                  </Typography>
                </Box>
              </Box>
              <FormGroup>
                {['dashboard', 'salesboard', 'users'].map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Checkbox
                        checked={newUser.permissions[permission]}
                        onChange={() => handlePermissionChange(permission)}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked': {
                            color: '#4f46e5',
                          },
                        }}
                      />
                    }
                    label={permission.charAt(0).toUpperCase() + permission.slice(1)}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9rem',
                        color: '#475569'
                      }
                    }}
                  />
                ))}
              </FormGroup>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          pt: 2,
          borderTop: '1px solid #e2e8f0',
          gap: 1
        }}
      >
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            color: '#64748b',
            borderColor: '#e2e8f0',
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#4f46e5',
            '&:hover': { bgcolor: '#4338ca' },
            px: 3,
            py: 1
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <span>Adding...</span>
            </Box>
          ) : (
            'Add User'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;